import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetCheckinDefinitionsByTopic } from "../../../../../api/queries";
import {
  useActivateCheckinDefinition,
  useDeleteCheckinDefinition,
  useUpdateCheckinDefinition,
} from "../../../../../api/mutations";
import { useNotification } from "../../../../../hooks/useNotification";
import ConfirmationBox from "../../../../../componentLibrary/ConfirmationBox";
import RenameModal from "../../../../../componentLibrary/RenameModal";
import Spinner from "../../../../../componentLibrary/Spinner";
import { DefinitionRow } from "../../components/DefinitionRow";
import type { ToolboxCheckinDefinitionI } from "../../../../../types";

interface Props {
  topicId: string;
  topicTitle: string;
}

export default function CheckinDefinitionList({ topicId, topicTitle }: Props) {
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const [activateTarget, setActivateTarget] =
    useState<ToolboxCheckinDefinitionI | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<ToolboxCheckinDefinitionI | null>(null);
  const [renameTarget, setRenameTarget] =
    useState<ToolboxCheckinDefinitionI | null>(null);

  const {
    data: definitions,
    isLoading,
    isError,
  } = useGetCheckinDefinitionsByTopic(topicId);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["toolboxCheckinDefinitions", topicId],
    });

  const activateMutation = useActivateCheckinDefinition({
    onSuccess: async () => {
      notify("success", "Check-in definition activated");
      setActivateTarget(null);
      await invalidate();
    },
    onError: () => notify("error", "Failed to activate check-in definition"),
  });

  const deleteMutation = useDeleteCheckinDefinition({
    onSuccess: async () => {
      notify("success", "Check-in definition deleted");
      setDeleteTarget(null);
      await invalidate();
    },
    onError: () => notify("error", "Failed to delete check-in definition"),
  });

  const renameMutation = useUpdateCheckinDefinition({
    onSuccess: async () => {
      notify("success", "Definition name updated");
      setRenameTarget(null);
      await invalidate();
    },
    onError: () => notify("error", "Failed to update name"),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner className="w-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-salmon-400 py-2">
        Failed to load check-in definitions.
      </p>
    );
  }

  const defLabel = (def: ToolboxCheckinDefinitionI) =>
    def.displayName?.trim() || `v${def.version}`;

  return (
    <div className="space-y-3 mt-6">
      {definitions?.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">
          No definition versions yet. Create one to start building the{" "}
          {topicTitle} check-in.
        </p>
      ) : (
        <div className="space-y-2">
          {definitions?.map((def) => {
            const ratingLabel = def.ratingEnabled
              ? `Rating 1–${def.ratingScale ?? "?"}`
              : "No rating";

            return (
              <DefinitionRow
                key={def._id}
                definition={def}
                editPath={`/admin/toolboxes/${topicId}/checkin-definitions/${def._id}/edit`}
                onActivate={() => setActivateTarget(def)}
                activating={
                  activateMutation.isPending && activateTarget?._id === def._id
                }
                onDelete={() => setDeleteTarget(def)}
                deleting={
                  deleteMutation.isPending && deleteTarget?._id === def._id
                }
                onRename={() => setRenameTarget(def)}
                extraMeta={
                  <span className="text-xs text-gray-400 hidden sm:block">
                    · {ratingLabel}
                  </span>
                }
              />
            );
          })}
        </div>
      )}

      <RenameModal
        isOpen={!!renameTarget}
        title="Rename definition"
        subtitle={
          renameTarget
            ? `Leave blank to use the default label (v${renameTarget.version}).`
            : undefined
        }
        initialValue={renameTarget?.displayName ?? ""}
        placeholder={`e.g. Spring rollout · v${renameTarget?.version ?? "1"}`}
        loading={renameMutation.isPending}
        onClose={() => {
          if (!renameMutation.isPending) setRenameTarget(null);
        }}
        onSave={(trimmed) => {
          if (!renameTarget) return;
          renameMutation.mutate({
            definitionId: renameTarget._id,
            displayName: trimmed.length > 0 ? trimmed : null,
          });
        }}
      />

      <ConfirmationBox
        isOpen={!!activateTarget}
        title="Activate this definition?"
        message={`${activateTarget ? defLabel(activateTarget) : ""} will become the live check-in definition for all users.`}
        confirmText="Activate"
        cancelText="Cancel"
        onConfirm={() =>
          activateTarget && activateMutation.mutate(activateTarget._id)
        }
        onCancel={() => setActivateTarget(null)}
        onCloseBox={() => setActivateTarget(null)}
      />

      <ConfirmationBox
        isOpen={!!deleteTarget}
        title="Delete this definition?"
        message={`${deleteTarget ? defLabel(deleteTarget) : ""} will be permanently deleted. This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        danger
        onConfirm={() =>
          deleteTarget && deleteMutation.mutate(deleteTarget._id)
        }
        onCancel={() => setDeleteTarget(null)}
        onCloseBox={() => setDeleteTarget(null)}
      />
    </div>
  );
}
